import type { DocumentSource } from '@hvac/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { documentKeys } from '../../shared/api/document-queries.js';
import { generatePreview, readPdfPageCount } from '../../shared/upload/preview.js';
import { hashFile, sniffMimeType } from '../../shared/upload/hash.js';
import { createUploadTransport } from '../../shared/upload/transport.js';
import { createUploadManager, type UploadItem } from '../../shared/upload/upload-manager.js';

/**
 * React binding for the upload queue.
 *
 * The manager itself is framework-free and holds all the state; this hook
 * mirrors it into React and invalidates the document list as uploads land.
 *
 * Invalidation is debounced. A hundred files completing means a hundred
 * completion callbacks, and refetching the list a hundred times would make the
 * page slower the more successful the upload was.
 */

export interface UseUploadQueueOptions {
  readonly organizationId: string;
  readonly source?: DocumentSource;
}

export interface UploadQueue {
  readonly items: readonly UploadItem[];
  readonly activeCount: number;
  readonly completedCount: number;
  readonly failedCount: number;
  readonly duplicateCount: number;
  readonly totalProgress: number;
  /** Returns the items it queued, in the order the files were given. */
  add(files: readonly File[]): UploadItem[];
  retry(itemId: string): void;
  overrideDuplicate(itemId: string, reason: string): void;
  cancel(itemId: string): void;
  cancelAll(): void;
  clearFinished(): void;
}

const ACTIVE_STATUSES = new Set(['queued', 'hashing', 'requesting', 'uploading', 'finalising']);

export function useUploadQueue(options: UseUploadQueueOptions): UploadQueue {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<readonly UploadItem[]>([]);
  // The debounce timer lives in the manager's own closure rather than a ref.
  // Its lifetime is exactly the manager's, which is what it should be, and it
  // keeps a ref out of the render path, where React forbids reading one.
  const instance = useMemo(() => {
    // A mutable holder rather than a captured `let`: React's rules of hooks
    // forbid reassigning a variable that outlives render, and a ref cannot be
    // read from the render path either. The timer belongs to the manager, so
    // it lives alongside it.
    const timer: { handle: number | undefined } = { handle: undefined };

    const manager = createUploadManager({
      organizationId: options.organizationId,
      source: options.source ?? detectSource(),
      transport: createUploadTransport(options.organizationId),
      hashFile,
      sniffMimeType,
      generatePreview,
      readPageCount: readPdfPageCount,
      onChange: setItems,
      // A hundred files completing must not mean a hundred refetches; the page
      // would get slower the more successful the upload was.
      onCompleted: () => {
        window.clearTimeout(timer.handle);
        timer.handle = window.setTimeout(() => {
          void queryClient.invalidateQueries({
            queryKey: documentKeys.all(options.organizationId),
          });
        }, 750);
      },
    });

    return { manager, dispose: () => window.clearTimeout(timer.handle) };
  }, [options.organizationId, options.source, queryClient]);

  const manager = instance.manager;

  // Leaving mid-upload loses work that cannot be resumed byte-for-byte, so the
  // browser's own confirmation is the honest thing to show.
  useEffect(() => {
    const inFlight = items.some((item) => ACTIVE_STATUSES.has(item.status));
    if (!inFlight) return undefined;

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [items]);

  useEffect(() => instance.dispose, [instance]);

  const counts = useMemo(() => {
    let active = 0;
    let completed = 0;
    let failed = 0;
    let duplicate = 0;
    let progressSum = 0;

    for (const item of items) {
      if (ACTIVE_STATUSES.has(item.status)) active += 1;
      if (item.status === 'completed') completed += 1;
      if (item.status === 'failed') failed += 1;
      if (item.status === 'duplicate') duplicate += 1;
      progressSum += item.status === 'completed' ? 1 : item.progress;
    }

    return {
      active,
      completed,
      failed,
      duplicate,
      totalProgress: items.length === 0 ? 0 : progressSum / items.length,
    };
  }, [items]);

  return {
    items,
    activeCount: counts.active,
    completedCount: counts.completed,
    failedCount: counts.failed,
    duplicateCount: counts.duplicate,
    totalProgress: counts.totalProgress,
    add: useCallback((files) => manager.add(files), [manager]),
    retry: useCallback((id) => manager.retry(id), [manager]),
    overrideDuplicate: useCallback(
      (id, reason) => manager.overrideDuplicate(id, reason),
      [manager],
    ),
    cancel: useCallback((id) => manager.cancel(id), [manager]),
    cancelAll: useCallback(() => manager.cancelAll(), [manager]),
    clearFinished: useCallback(() => manager.clearFinished(), [manager]),
  };
}

/**
 * Best-effort source detection.
 *
 * Recorded so "why do mobile uploads fail more often" is answerable later. It
 * cannot be reconstructed after the fact, and it costs one line here.
 */
function detectSource(): DocumentSource {
  if (typeof navigator === 'undefined') return 'web_desktop';
  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  return isMobile ? 'web_mobile' : 'web_desktop';
}
